/**
 * product-swatches.js
 *
 * Handles colour swatch interactions for:
 *   1. Product cards  (.wl-product-card)  — on collection grid + homepage
 *   2. PDP           (.wl-pdp-swatches-root) — product page
 *
 * Vanilla JS, no dependencies, IIFE-scoped.
 *
 * Behaviour:
 *   Card:  hover = preview image, mouseleave = restore selection,
 *          click = select variant + keep image + update link href
 *   PDP:   hover = preview image only (no variant change),
 *          click = trigger Savor's hidden radio → price/ATC/URL all update,
 *          syncs back if Savor's own picker fires variant:changed
 */

(function () {
  'use strict';

  var FADE_MS = 150;

  // ── Crossfade helper ────────────────────────────────────────────────────
  function swapImage(img, newSrc) {
    if (!img || !newSrc) return;
    // normalise — strip width params for comparison
    var currentBase = img.src.split('?')[0];
    var newBase = newSrc.split('?')[0];
    if (currentBase === newBase) return;

    img.classList.add('wl-swatchable-img', 'is-fading');

    var done = false;
    function swap() {
      if (done) return;
      done = true;
      img.src = newSrc;
      img.classList.remove('is-fading');
    }
    img.addEventListener('transitionend', swap, { once: true });
    setTimeout(swap, FADE_MS + 30); // fallback
  }

  // ── Preload images for instant hover ───────────────────────────────────
  function preload(src) {
    if (!src) return;
    var img = new Image();
    img.src = src;
  }

  // ── Mark one swatch active, clear others ───────────────────────────────
  function setActiveInGroup(swatches, active) {
    swatches.forEach(function (s) {
      s.setAttribute('aria-pressed', s === active ? 'true' : 'false');
      s.classList.toggle('is-active', s === active);
    });
  }

  // ══════════════════════════════════════════════════════════════════════════
  // 1. PRODUCT CARD
  // ══════════════════════════════════════════════════════════════════════════

  function initCard(cardEl) {
    var group = cardEl.querySelector('.wl-swatches--card');
    if (!group) return;

    var swatches = Array.from(group.querySelectorAll('.wl-swatch-btn'));
    if (!swatches.length) return;

    // Find the card's primary <img> — traverse up then look for first img
    // Savor cards: image is in a sibling container above the info panel.
    // We walk up from the group to the card root and find the first img.
    var img = (function () {
      var el = group;
      var root = null;
      while (el && el !== document.body) {
        if (el === cardEl) { root = el; break; }
        el = el.parentElement;
      }
      if (!root) return null;
      return (
        root.querySelector('img[loading="lazy"]') ||
        root.querySelector('.media img') ||
        root.querySelector('figure img') ||
        root.querySelector('img')
      );
    }());

    // Product link — update ?variant= on selection
    var productLink = cardEl.querySelector('a[href*="/products/"]');

    // State
    var selectedSwatch = null;
    var selectedSrc = null;

    function select(swatch) {
      setActiveInGroup(swatches, swatch);
      selectedSwatch = swatch;
      selectedSrc = swatch.dataset.variantImage || null;

      // Update the card link to pre-select this variant on the PDP
      if (productLink && swatch.dataset.variantId) {
        try {
          var url = new URL(productLink.href, window.location.origin);
          url.searchParams.set('variant', swatch.dataset.variantId);
          productLink.href = url.toString();
        } catch (e) {}
      }
    }

    // Preload all variant images up front
    swatches.forEach(function (s) { preload(s.dataset.variantImage); });

    // Wire events
    swatches.forEach(function (swatch) {
      swatch.addEventListener('click', function () {
        select(swatch);
        swapImage(img, swatch.dataset.variantImage);
      });

      swatch.addEventListener('mouseenter', function () {
        swapImage(img, swatch.dataset.variantImage);
      });

      swatch.addEventListener('mouseleave', function () {
        if (selectedSrc) swapImage(img, selectedSrc);
      });

      // Keyboard
      swatch.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); swatch.click(); }
        // Arrow key navigation within the group
        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
          e.preventDefault();
          var idx = swatches.indexOf(swatch);
          var next = e.key === 'ArrowRight'
            ? swatches[(idx + 1) % swatches.length]
            : swatches[(idx - 1 + swatches.length) % swatches.length];
          next.focus();
          next.click();
        }
      });
    });

    // Initialise: select first swatch (or the one already marked active)
    var initialActive = group.querySelector('.wl-swatch-btn.is-active, .wl-swatch-btn[aria-pressed="true"]') || swatches[0];
    if (initialActive) select(initialActive);
  }

  // ══════════════════════════════════════════════════════════════════════════
  // 2. PDP
  // ══════════════════════════════════════════════════════════════════════════

  function initPDP() {
    var root = document.querySelector('.wl-pdp-swatches-root');
    if (!root) return;

    var group = root.querySelector('.wl-swatches--pdp');
    if (!group) return;

    var swatches = Array.from(group.querySelectorAll('.wl-swatch-btn'));
    if (!swatches.length) return;

    var selectedNameEl = root.querySelector('.wl-swatches__selected-name');

    var selectedSwatch = null;
    var selectedSrc = null;

    // ── Find Savor's hidden variant radio for a given variantId ───────────
    function findVariantRadio(variantId) {
      return (
        document.querySelector('input[type="radio"][value="' + variantId + '"]') ||
        document.querySelector('input[name="id"][value="' + variantId + '"]') ||
        document.querySelector('[data-variant-id="' + variantId + '"] input') ||
        null
      );
    }

    // ── Find the currently visible gallery image ─────────────────────────
    // Tries a cascade of Savor selectors (robust across theme versions).
    function getGalleryImg() {
      var candidates = [
        // Savor 3.x: slideshow active slide
        '.product-media-gallery .slideshow__slide--active img',
        '.product-media-gallery .slideshow__slide.is-active img',
        // Generic media wrappers
        '.product-media-gallery .media--image img',
        '.product-media-gallery .media img',
        // Fallback: any img in the gallery component
        'product-media-gallery img',
        '[data-product-media-gallery] img',
        // Last resort: first img near the product form
        '.product__media img',
        '.product-single__photo img'
      ];
      for (var i = 0; i < candidates.length; i++) {
        var el = document.querySelector(candidates[i]);
        if (el) return el;
      }
      return null;
    }

    // ── Trigger Savor's full variant change pipeline ──────────────────────
    // Clicking the radio fires Savor's variant-picker.js handlers which:
    //   - swaps the gallery to the variant's media
    //   - updates price
    //   - updates ATC availability
    //   - pushes ?variant= to URL history
    function triggerSavorVariantChange(variantId) {
      var radio = findVariantRadio(variantId);
      if (radio) {
        radio.checked = true;
        // Dispatch both events — Savor listens to 'change', some themes use 'input'
        radio.dispatchEvent(new Event('change', { bubbles: true }));
        radio.dispatchEvent(new Event('input', { bubbles: true }));
      } else {
        // Fallback: push URL param; Savor may pick it up via popstate
        try {
          var url = new URL(window.location.href);
          url.searchParams.set('variant', variantId);
          window.history.replaceState({}, '', url.toString());
        } catch (e) {}
      }
    }

    // ── Set our custom active state ───────────────────────────────────────
    function setActive(swatch) {
      setActiveInGroup(swatches, swatch);
      selectedSwatch = swatch;
      selectedSrc = swatch.dataset.variantImage || null;
      if (selectedNameEl) {
        selectedNameEl.textContent = swatch.getAttribute('aria-label') || '';
      }
    }

    // Preload all variant images
    swatches.forEach(function (s) { preload(s.dataset.variantImage); });

    // Wire events
    swatches.forEach(function (swatch) {
      // CLICK: select variant (Savor handles gallery/price/URL)
      swatch.addEventListener('click', function () {
        setActive(swatch);
        if (swatch.dataset.variantId) {
          triggerSavorVariantChange(swatch.dataset.variantId);
        }
      });

      // HOVER: preview image only — no variant/price change
      swatch.addEventListener('mouseenter', function () {
        var img = getGalleryImg();
        if (img && swatch.dataset.variantImage) {
          img.classList.add('wl-swatchable-img');
          swapImage(img, swatch.dataset.variantImage);
        }
      });

      swatch.addEventListener('mouseleave', function () {
        if (selectedSrc) {
          var img = getGalleryImg();
          if (img) swapImage(img, selectedSrc);
        }
      });

      // Keyboard
      swatch.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); swatch.click(); }
        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
          e.preventDefault();
          var idx = swatches.indexOf(swatch);
          var next = e.key === 'ArrowRight'
            ? swatches[(idx + 1) % swatches.length]
            : swatches[(idx - 1 + swatches.length) % swatches.length];
          next.focus();
          next.click();
        }
      });
    });

    // ── Initialise from URL ?variant= ─────────────────────────────────────
    (function () {
      var urlVariantId = null;
      try {
        urlVariantId = new URLSearchParams(window.location.search).get('variant');
      } catch (e) {}

      var initial = null;
      if (urlVariantId) {
        initial = group.querySelector('.wl-swatch-btn[data-variant-id="' + urlVariantId + '"]');
      }
      if (!initial) {
        initial = group.querySelector('.wl-swatch-btn[aria-pressed="true"]') || swatches[0];
      }
      if (initial) setActive(initial);
    }());

    // ── Sync if Savor's own picker fires externally ───────────────────────
    // (e.g. user uses Savor's hidden select/radio directly)
    document.addEventListener('variant:changed', function (e) {
      var id = String(
        (e.detail && (e.detail.variantId || (e.detail.variant && e.detail.variant.id))) || ''
      );
      if (!id) return;
      var match = group.querySelector('.wl-swatch-btn[data-variant-id="' + id + '"]');
      if (match && match !== selectedSwatch) setActive(match);
    });
  }

  // ══════════════════════════════════════════════════════════════════════════
  // Init
  // ══════════════════════════════════════════════════════════════════════════

  var COLOUR_HEX = {
    Black: '#1a1a1a', Beige: '#d9c7a8', Sakura: '#f3c9d3', Sage: '#9aa783'
  };

  /* Populate empty card swatch containers that have data-product-handle */
  function populateCardSwatches() {
    var empties = document.querySelectorAll('.wl-swatches--card[data-product-handle]:empty');
    empties.forEach(function (container) {
      var handle = container.dataset.productHandle;
      if (!handle) return;
      fetch('/products/' + handle + '.js')
        .then(function (r) { return r.json(); })
        .then(function (data) {
          var ci = 0;
          var opts = (data.options || []).map(function (o) { return (o.name || '').toLowerCase(); });
          var idx = opts.indexOf('colour'); if (idx < 0) idx = opts.indexOf('color');
          if (idx >= 0) ci = idx;
          var seen = {};
          (data.variants || []).forEach(function (v) {
            var colour = v.options ? v.options[ci] : v['option' + (ci + 1)];
            if (!colour || seen[colour]) return;
            seen[colour] = true;
            var img = v.featured_image;
            var imgUrl = img ? img.src.replace(/^\/\//, 'https://') + (img.src.indexOf('?') > -1 ? '&' : '?') + 'width=800' : '';
            var hex = COLOUR_HEX[colour] || '#cccccc';
            var btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'wl-swatch-btn';
            btn.style.backgroundColor = hex;
            btn.setAttribute('aria-label', colour);
            btn.setAttribute('aria-pressed', 'false');
            btn.dataset.variantId = String(v.id);
            btn.dataset.variantImage = imgUrl;
            container.appendChild(btn);
            if (imgUrl) { var pi = new Image(); pi.src = imgUrl; }
          });
          /* Wire up the card now that buttons exist */
          var card = container.closest('product-card, .wl-product-card');
          if (card) initCard(card);
        })
        .catch(function () {});
    });
  }

  function init() {
    // Savor uses <product-card> custom element; homepage cards use .wl-product-card
    document.querySelectorAll('product-card, .wl-product-card').forEach(initCard);
    // Populate any empty swatch containers that have a product handle
    populateCardSwatches();
    // PDP (single instance per page)
    initPDP();
  }

  // Run after DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init after Shopify section hot-reloads (theme editor)
  document.addEventListener('shopify:section:load', function () {
    document.querySelectorAll('product-card, .wl-product-card').forEach(initCard);
    initPDP();
  });

}());
